import { useState, useEffect } from 'react';
import { Col, Divider, Input, Row, Skeleton } from 'antd';
import { useDispatch } from 'react-redux';
import { isNull } from 'lodash';
import { updateMemberId } from 'modules/auth/slices/idCardSlice';
import { useAppSelector } from 'hooks/redux';
import OverflowPopover from 'components/PlanYearOverFlowPopover/OverflowPopover';
import { CoverageDetails, IndividualName } from 'modules/auth/types/types';
import { formatEffectiveDates } from 'utils/commonUtil';
import { getCardPlanYear } from 'modules/auth/util/idCardPlanYears';
import { getText } from 'utils/stringUtil';
import {
  YOU_HAVE_WAIVED_PLANS_FOR_THIS_BENEFIT,
  BENEFITS_ARE_NOT_OFFERED_CURRENTLY,
  ID_CARD_NAME,
  ID_CARD_PRIMARY_SUBSCRIBER,
  MEMBER_ID,
  ID_CARD_GROUP_ID,
  ID_CARD_EFFECTIVE_DATE,
  ID_CARD_MEMBERS_AND_PROVIDERS,
  NETWORK_PROVIDERS_MUST_CALL,
  ID_CARD_CARRIER_PHONE,
  ADD_ID,
  SAVE,
  ID_CARD_PHONE,
  ID_CARD_RX_BIN,
  ID_CARD_RX_PCN,
  ID_CARD_RX_GROUP,
} from 'constants/languageConstants';
import { PLAN_VIEW } from 'modules/auth/constants/authConstants';
import EditIcon from 'images/icon-login.svg';

import styles from './idCard.module.less';

export type IDDataType = {
  id: string;
  benefitKind: string;
  planId: string;
  planName: string;
  individualId: string;
  subscriberId: string;
  employerId: string;
  individualName: IndividualName;
  subscriberName: IndividualName;
  dob: string | null;
  coverageDetails: CoverageDetails;
  tier: string | null;
  planYearId: string;
};

type IDType = {
  isFlipped: boolean;
  data: IDDataType;
  employerData: any;
  isWaived: boolean;
  isEmpty: boolean;
  isLoading: boolean;
  hasPlan: boolean;
  view: string;
  isDependent: boolean;
};

type Edit = {
  editModeOn: boolean;
  editValue: string;
};

const IDCard = ({
  isFlipped,
  data,
  employerData,
  isWaived,
  isLoading,
  isEmpty,
  hasPlan = true,
  view,
  isDependent = false,
}: IDType) => {
  const currentPlanYear = useAppSelector(
    (state) => state.idCard.currentPlanYear
  );

  const employee = useAppSelector((state) => state.idCard.currentEmployee);
  const language = useAppSelector((state) => state.benguide.data?.language);
  const dispatch = useDispatch();
  const planYearsById = useAppSelector((state) => state.idCard.planYearsById);
  const [editMemberId, setSetEditMemberId] = useState<Edit>({
    editModeOn: false,
    editValue: data?.coverageDetails?.memberIdNumber || '',
  });

  const cardPlanYear = getCardPlanYear(
    planYearsById,
    data?.planYearId,
    currentPlanYear
  );
  const getPlanYearDates = formatEffectiveDates(
    cardPlanYear?.startDate,
    cardPlanYear?.endDate
  );

  const getPlanViewTitle = (key: string) => {
    if (PLAN_VIEW[key]) {
      return getText(PLAN_VIEW[key].value, language);
    }
    return '';
  };

  const onEditClick = () => {
    setSetEditMemberId({
      editValue: data?.coverageDetails?.memberIdNumber || '',
      editModeOn: true,
    });
  };

  const handleOnEditSave = () => {
    if (!isLoading && !isEmpty) {
      dispatch(
        updateMemberId(
          employee?.individualId,
          data.planYearId,
          data.benefitKind,
          editMemberId.editValue
        )
      );
    }

    setSetEditMemberId({
      ...editMemberId,
      editModeOn: false,
    });
  };

  useEffect(() => {
    if (editMemberId.editModeOn) {
      setSetEditMemberId({
        ...editMemberId,
        editModeOn: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFlipped, view, data]);

  return (
    <div className={`${styles.flipCard} ${isFlipped ? styles.flipped : ''}`}>
      {isEmpty || isLoading ? (
        <div className={styles.waivedContainer}>
          <Skeleton paragraph={{ rows: 4 }} />
        </div>
      ) : isWaived ? (
        <div className={styles.waivedContainer}>
          {getText(YOU_HAVE_WAIVED_PLANS_FOR_THIS_BENEFIT, language)}
        </div>
      ) : !hasPlan ? (
        <div className={styles.waivedContainer}>
          {`${getPlanViewTitle(view)} ${getText(
            BENEFITS_ARE_NOT_OFFERED_CURRENTLY,
            language
          )}`}
        </div>
      ) : (
        <div className={styles.flipCardInner}>
          <div className={styles.flipCardFront}>
            <div className={styles.cardTopContainer}>
              <Row justify="space-between">
                <Col>
                  {data?.coverageDetails?.logoUrl && (
                    <img
                      src={data?.coverageDetails?.logoUrl}
                      className={styles.employerLogo}
                      alt="employer"
                    />
                  )}
                </Col>
                <Col>
                  {employerData?.logoUrl && (
                    <img
                      src={employerData?.logoUrl}
                      className={styles.employerLogo}
                      alt="client"
                    />
                  )}
                </Col>
              </Row>
              <h1 className={styles.heading}>
                <OverflowPopover maxWidth="490px">
                  {data?.planName}
                </OverflowPopover>
              </h1>

              <p className={styles.subHeading}>
                <b>
                  {' '}
                  {getText(ID_CARD_EFFECTIVE_DATE, language)} :{' '}
                  {`${getPlanYearDates?.startDate}`}{' '}
                </b>
              </p>
            </div>
            <div className={styles.cardBottomContainer}>
              <Row gutter={24}>
                <Col span={12} className={styles.labelText}>
                  <b>{getText(ID_CARD_NAME, language)}</b>
                </Col>
                <Col span={12}>
                  <OverflowPopover maxWidth="150px">
                    {data?.individualName?.firstName &&
                    data?.individualName?.lastName ? (
                      <>
                        {`${data?.individualName?.firstName} ${data?.individualName?.lastName}`}
                      </>
                    ) : (
                      '-'
                    )}
                  </OverflowPopover>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={12} className={styles.labelText}>
                  <b>{getText(ID_CARD_PRIMARY_SUBSCRIBER, language)}</b>
                </Col>
                <Col span={12}>
                  <OverflowPopover maxWidth="150px">
                    {data?.subscriberName?.firstName &&
                    data?.subscriberName?.lastName ? (
                      <>
                        {`${data?.subscriberName?.firstName} ${data?.subscriberName?.lastName}`}
                      </>
                    ) : (
                      '-'
                    )}
                  </OverflowPopover>
                </Col>
              </Row>
              <Divider className={styles.containerDivider} />
              <Row gutter={32}>
                <Col span={12}>
                  <Row gutter={4}>
                    <Col span={9} className={styles.labelText}>
                      <b>{getText(MEMBER_ID, language)}</b>
                    </Col>
                    <Col span={15}>
                      {!isDependent ? (
                        <div className={styles.editcontainer}>
                          <>
                            {editMemberId.editModeOn ? (
                              <Input
                                className={styles.editInput}
                                type="input"
                                name="memberId"
                                value={editMemberId.editValue}
                                onChange={(e) =>
                                  setSetEditMemberId({
                                    ...editMemberId,
                                    editValue: e.currentTarget.value,
                                  })
                                }
                              />
                            ) : (
                              <OverflowPopover maxWidth="150px">
                                {data?.coverageDetails?.memberIdNumber ? (
                                  <>
                                    {`${
                                      view === PLAN_VIEW.MEDICAL.type &&
                                      !isNull(
                                        data?.coverageDetails?.alphaPrefix
                                      )
                                        ? data?.coverageDetails?.alphaPrefix
                                        : ''
                                    }${
                                      data?.coverageDetails?.memberIdNumber
                                    }`}{' '}
                                  </>
                                ) : (
                                  <span
                                    className={styles.linkText}
                                    onClick={() => onEditClick()}
                                  >
                                    <b>{getText(ADD_ID, language)}</b>
                                  </span>
                                )}
                              </OverflowPopover>
                            )}
                          </>
                          <>
                            {editMemberId.editModeOn ? (
                              <span
                                className={styles.linkText}
                                onClick={() => handleOnEditSave()}
                              >
                                <b>{getText(SAVE, language)}</b>
                              </span>
                            ) : (
                              <img
                                src={EditIcon}
                                alt="edit"
                                className={styles.editIcon}
                                onClick={() => onEditClick()}
                              />
                            )}
                          </>
                        </div>
                      ) : (
                        <>
                          <OverflowPopover maxWidth="150px">
                            {data?.coverageDetails?.memberIdNumber ? (
                              <>
                                {`${
                                  view === PLAN_VIEW.MEDICAL.type &&
                                  !isNull(data?.coverageDetails?.alphaPrefix)
                                    ? data?.coverageDetails?.alphaPrefix
                                    : ''
                                }${data?.coverageDetails?.memberIdNumber}`}{' '}
                              </>
                            ) : (
                              '-'
                            )}
                          </OverflowPopover>
                        </>
                      )}
                    </Col>
                  </Row>

                  <Row gutter={4}>
                    <Col span={9} className={styles.labelText}>
                      <b>{getText(ID_CARD_GROUP_ID, language)}</b>
                    </Col>
                    <Col span={15}>
                      {data?.coverageDetails?.groupIdNumber ? (
                        <OverflowPopover maxWidth="150px">
                          {data?.coverageDetails?.groupIdNumber}
                        </OverflowPopover>
                      ) : (
                        '-'
                      )}
                    </Col>
                  </Row>

                  <Row gutter={4}>
                    <Col span={9} className={styles.labelText}>
                      <b>{getText(ID_CARD_PHONE, language)}</b>
                    </Col>
                    <Col span={15}>
                      {data?.coverageDetails?.carrierPhone ? (
                        <OverflowPopover maxWidth="150px">
                          <a
                            className={styles.linkText}
                            href={`tel:${data?.coverageDetails?.carrierPhone}`}
                          >
                            <b> {data?.coverageDetails?.carrierPhone}</b>
                          </a>
                        </OverflowPopover>
                      ) : (
                        '-'
                      )}
                    </Col>
                  </Row>
                </Col>
                {view === PLAN_VIEW.MEDICAL.type && (
                  <Col span={12}>
                    <Row>
                      <Col span={9} className={styles.labelText}>
                        <b>{getText(ID_CARD_RX_BIN, language)}</b>
                      </Col>

                      <Col span={15}>
                        {data?.coverageDetails?.rxBINumber ? (
                          <OverflowPopover maxWidth="150px">
                            {data?.coverageDetails?.rxBINumber}
                          </OverflowPopover>
                        ) : (
                          '-'
                        )}
                      </Col>
                    </Row>

                    <Row>
                      <Col span={9} className={styles.labelText}>
                        <b>{getText(ID_CARD_RX_PCN, language)}</b>
                      </Col>
                      <Col span={15}>
                        {data?.coverageDetails?.rxPCNumber ? (
                          <OverflowPopover maxWidth="150px">
                            {data?.coverageDetails?.rxPCNumber}
                          </OverflowPopover>
                        ) : (
                          '-'
                        )}
                      </Col>
                    </Row>

                    <Row>
                      <Col span={9} className={styles.labelText}>
                        <b>{getText(ID_CARD_RX_GROUP, language)}</b>
                      </Col>
                      <Col span={15}>
                        {data?.coverageDetails?.rxGroupNumber ? (
                          <OverflowPopover maxWidth="150px">
                            {data?.coverageDetails?.rxGroupNumber}
                          </OverflowPopover>
                        ) : (
                          '-'
                        )}
                      </Col>
                    </Row>
                  </Col>
                )}
              </Row>
            </div>
          </div>
          <div className={styles.flipCardBack}>
            <div className={styles.cardTopContainer}>
              <Row justify="space-between">
                <Col>
                  {data?.coverageDetails?.logoUrl && (
                    <img
                      src={data?.coverageDetails?.logoUrl}
                      className={styles.employerLogo}
                      alt="employer"
                    />
                  )}
                </Col>
                <Col>
                  {employerData?.logoUrl && (
                    <img
                      src={employerData?.logoUrl}
                      className={styles.employerLogo}
                      alt="client"
                    />
                  )}
                </Col>
              </Row>
            </div>{' '}
            <div className={styles.cardBackBottomContainer}>
              <Row>
                <Col span={24}>
                  <h3 className={styles.backHeader}>
                    {getText(ID_CARD_MEMBERS_AND_PROVIDERS, language)}
                  </h3>
                  <span className={styles.subHeading}>
                    {getText(NETWORK_PROVIDERS_MUST_CALL, language)}
                  </span>
                </Col>
              </Row>
              <Divider className={styles.containerDivider} />

              <Row gutter={32} justify="space-between">
                <Col span={12} className={styles.labelText}>
                  <Row justify="space-between">
                    <b>{getText(ID_CARD_CARRIER_PHONE, language)}:</b>
                    {!data?.coverageDetails?.carrierPhone && '-'}
                  </Row>
                </Col>
                <Col>
                  {data?.coverageDetails?.carrierPhone && (
                    <a
                      className={styles.linkText}
                      href={`tel:${data?.coverageDetails?.carrierPhone}`}
                    >
                      <b> {data?.coverageDetails?.carrierPhone}</b>
                    </a>
                  )}
                </Col>
              </Row>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IDCard;
